import { PDFDocument } from 'pdf-lib';
import { Quote, TenderDocument } from '../../types';
import { fetchTenderDocuments, getTenderDocumentSignedUrl } from '../crmService';

// A4 in PostScript-Punkten (pdf-lib rechnet in pt, nicht mm).
const A4_PT: [number, number] = [595.28, 841.89];

/**
 * Wandelt PDF-Bytes in Base64 um. Chunked, weil String.fromCharCode mit einem
 * kompletten Mehr-MB-Array den Stack sprengt.
 */
const bytesToBase64 = (bytes: Uint8Array): string => {
  const CHUNK = 0x8000;
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
};

/** Base64 ohne data:-Praefix — so erwartet es der EML-Builder als Anhang. */
export const pdfBytesToBase64 = (bytes: Uint8Array): string => bytesToBase64(bytes);

export const pdfBytesToBlob = (bytes: Uint8Array): Blob =>
  new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });

export const pdfBytesToDataUri = (bytes: Uint8Array): string =>
  `data:application/pdf;base64,${bytesToBase64(bytes)}`;

export type LvOriginalMode = 'none' | 'append' | 'priced';

/**
 * Einbindungs-Modus fuers Original-LV. `details.lvOriginalMode` gewinnt;
 * das aeltere Boolean `attachOriginalLv` bleibt als Fallback lesbar
 * (true -> 'append').
 */
export const lvOriginalMode = (quote: Pick<Quote, 'id' | 'details'>): LvOriginalMode => {
  if (!quote.id) return 'none';
  const m = quote.details?.lvOriginalMode;
  if (m === 'append' || m === 'priced' || m === 'none') return m;
  return quote.details?.attachOriginalLv ? 'append' : 'none';
};

/** True, wenn das Original-LV in irgendeiner Form ins PDF eingebunden wird. */
export const wantsOriginalLv = (quote: Pick<Quote, 'id' | 'details'>): boolean =>
  lvOriginalMode(quote) !== 'none';

/**
 * Welches LV-Original gehoert ins PDF?
 *
 * `details.activeTenderDocumentId` gewinnt: das ist das Dokument, dessen
 * Positionen tatsaechlich uebernommen wurden. Jeder Import laedt sein PDF
 * sofort hoch — ohne den Verweis ersetzte ein abgebrochener oder falscher
 * Import das echte Original (Audit 24.09., Q10). Fehlt der Verweis
 * (Altbestand) oder zeigt er ins Leere, gilt wie bisher das juengste
 * 'lv'-Dokument (fetchTenderDocuments liefert aufsteigend nach Anlage).
 */
export const pickOriginalLv = (
  docs: TenderDocument[],
  activeId?: string | null,
): TenderDocument | undefined => {
  const lvs = docs.filter((d) => d.kind === 'lv');
  if (activeId) {
    const active = lvs.find((d) => d.id === activeId);
    if (active) return active;
  }
  return lvs[lvs.length - 1];
};

/** Laedt das aktive (sonst juengste) LV-Original (kind 'lv') aus dem Bucket. */
const loadOriginalLv = async (
  quote: Pick<Quote, 'id' | 'details'>,
): Promise<{ lv: TenderDocument; bytes: Uint8Array }> => {
  const docs = await fetchTenderDocuments(quote.id);
  const lv = pickOriginalLv(docs, quote.details?.activeTenderDocumentId);
  if (!lv) {
    throw new Error(
      'Original-LV anhängen ist aktiv, aber zu diesem Angebot ist kein Ausschreibungs-Dokument gespeichert.',
    );
  }
  const url = await getTenderDocumentSignedUrl(lv.filePath);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Original-LV konnte nicht geladen werden (HTTP ${res.status}).`);
  }
  return { lv, bytes: new Uint8Array(await res.arrayBuffer()) };
};

/**
 * Haengt das gespeicherte Original-Ausschreibungs-PDF (tender_documents,
 * kind 'lv') hinten an das generierte Angebots-PDF an: Angebot bleibt vorne,
 * das Original folgt unveraendert ab der naechsten Seite. Das Original wird
 * NICHT beschrieben — genau deshalb anhaengen statt Overlay.
 *
 * No-op (gibt die Eingabe-Bytes unveraendert zurueck), wenn das Flag
 * `details.attachOriginalLv` nicht gesetzt ist oder das Angebot noch keine id
 * hat. Ist das Flag gesetzt, aber das Original nicht ladbar, wird bewusst
 * geworfen statt still ein Angebot ohne LV auszuliefern.
 *
 * Bild-Uploads (JPEG/PNG — der Import erlaubt sie) werden je auf eine eigene
 * A4-Seite eingepasst.
 */
export const appendOriginalLvToPdf = async (
  quotePdfBytes: Uint8Array | ArrayBuffer,
  quote: Pick<Quote, 'id' | 'details'>,
): Promise<Uint8Array> => {
  const base = quotePdfBytes instanceof Uint8Array ? quotePdfBytes : new Uint8Array(quotePdfBytes);
  if (!wantsOriginalLv(quote)) return base;
  const { lv, bytes: originalBytes } = await loadOriginalLv(quote);
  return appendLvBytes(base, lv, originalBytes);
};

/** Haengt die Original-Bytes (PDF oder Bild) hinten an das Basis-PDF an. */
const appendLvBytes = async (
  base: Uint8Array,
  lv: TenderDocument,
  originalBytes: Uint8Array,
): Promise<Uint8Array> => {
  const merged = await PDFDocument.load(base);
  const mime = (lv.mimeType || '').toLowerCase();
  const isImage = mime.startsWith('image/') || /\.(png|jpe?g)$/i.test(lv.filePath);

  try {
    if (isImage) {
      const isPng = mime.includes('png') || /\.png$/i.test(lv.filePath);
      const img = isPng ? await merged.embedPng(originalBytes) : await merged.embedJpg(originalBytes);
      const page = merged.addPage(A4_PT);
      const scale = Math.min(A4_PT[0] / img.width, A4_PT[1] / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      page.drawImage(img, { x: (A4_PT[0] - w) / 2, y: (A4_PT[1] - h) / 2, width: w, height: h });
    } else {
      const src = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
      const pages = await merged.copyPages(src, src.getPageIndices());
      pages.forEach((p) => merged.addPage(p));
    }
  } catch (e) {
    throw new Error(
      `Original-LV "${lv.fileName || lv.filePath}" konnte nicht angehängt werden` +
        ' (beschädigt oder passwortgeschützt): ' +
        (e instanceof Error ? e.message : String(e)),
    );
  }

  return merged.save();
};

/**
 * Zentrale Einbindung des Original-LV nach Modus:
 *  - 'none'   -> Eingabe unveraendert zurueck
 *  - 'append' -> Original unveraendert hinten anhaengen
 *  - 'priced' -> Preise ins Original stempeln (lvPriceOverlay) und das
 *    bepreiste Original direkt NACH Seite 1 (Anschreiben) einfuegen — vor
 *    Zahlungsbedingungen/AGB/Datenschutz. Der Aufrufer generiert das Angebot
 *    dazu mit generateQuotePDF(..., { skipCalculation: true }).
 *
 * pdfjs haengt am priced-Modus und wird nur dann geladen (dynamischer Import,
 * haelt das Haupt-Bundle frei davon).
 */
export const applyOriginalLv = async (
  quotePdfBytes: Uint8Array | ArrayBuffer,
  quote: Quote,
): Promise<Uint8Array> => {
  const base = quotePdfBytes instanceof Uint8Array ? quotePdfBytes : new Uint8Array(quotePdfBytes);
  const mode = lvOriginalMode(quote);
  if (mode === 'none') return base;
  const { lv, bytes: originalBytes } = await loadOriginalLv(quote);
  if (mode === 'append') return appendLvBytes(base, lv, originalBytes);

  const { stampLvPrices } = await import('./lvPriceOverlay');
  const priced = await stampLvPrices(originalBytes, quote);
  const target = await PDFDocument.load(base);
  const src = await PDFDocument.load(priced);
  const pages = await target.copyPages(src, src.getPageIndices());
  pages.forEach((p, i) => target.insertPage(1 + i, p));
  return target.save();
};
